// [NEW FILE] PM-4: change the dates of a stage in a cycle that is already running.
// The backend freezes the dates of a launched cycle and offers per-appraisal deadline overrides instead
// (POST /records/{id}/deadline-overrides). This dialog applies the same override to EVERY employee in the cycle
// in one go, so for HR it works like "editing the cycle date". Finalised appraisals are skipped.
import { useEffect, useState } from "react";
import { FiCalendar } from "react-icons/fi";
import { Modal, Button, Banner, FormField, ProgressBar } from "../../../components/common";
import Select from "../../../components/common/Select";
import { getCycleMembers, changeActiveCycleDates, updateCycle } from "../../../services/cycleApi";
import { BACKEND } from "../../../config/backendFeatures";
import { overrideDeadline } from "../../../services/recordApi";
import { STAGE_LABELS } from "../../../utils/constants";
import { formatDate } from "../../../utils/format";
import { getErrorMessage } from "../../../utils/errors";

const STAGES = [
  ["goalSetting", "GOAL_SETTING"],
  ["selfAssessment", "SELF_ASSESSMENT"],
  ["managerReview", "MANAGER_ASSESSMENT"],
  ["hrFinalization", "HR_FINALIZATION"],
];

// [UPDATED] PM-4: opened from any stage row ("Edit dates"); a Draft cycle saves its dates directly (PUT), a running
// cycle uses the backend date change (or per-employee overrides as fallback).
export default function CycleDatesModal({ cycle, stageKey, onClose, onDone }) {
  const [form, setForm] = useState({ stage: "goalSetting", opensOn: "", closesOn: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const current = (key) => (cycle?.stages || []).find((s) => s.key === key) || {};
  useEffect(() => {
    if (!cycle) return;
    const key = stageKey || "goalSetting";
    const s = (cycle.stages || []).find((x) => x.key === key) || {};
    setForm({ stage: key, opensOn: s.startDate || "", closesOn: s.endDate || "", reason: "" });
    setResult(null); setError(""); setProgress(null);
  }, [cycle, stageKey]);
  const isDraft = cycle?.status === "Draft";

  const pickStage = (key) => {
    const s = current(key);
    setForm((f) => ({ ...f, stage: key, opensOn: s.startDate || "", closesOn: s.endDate || "" }));
  };

  const close = () => { if (!busy) onClose(); };

  const run = async () => {
    setError("");
    if (!form.reason.trim()) { setError("Please give a reason. Employees see the new dates; the reason is kept in the audit trail."); return; }
    if (!form.opensOn || !form.closesOn || form.closesOn < form.opensOn) { setError("Pick an opening date and a closing date on or after it."); return; }
    if (form.closesOn > cycle.endDate || form.opensOn < cycle.startDate) { setError(`The dates must lie inside the cycle (${formatDate(cycle.startDate)} to ${formatDate(cycle.endDate)}).`); return; }
    setBusy(true);
    try {
      // With REACT_APP_BACKEND_ACTIVE_CYCLE_DATES=true the backend changes the cycle and all appraisals in one call.
      if (isDraft) {
        const stages = cycle.stages.map((s) => (s.key === form.stage ? { ...s, startDate: form.opensOn, endDate: form.closesOn } : s));
        await updateCycle(cycle.id, { ...cycle, stages, reason: `Stage dates changed: ${form.reason.trim()}` });
        setResult({ wholeCycle: true, draft: true, changed: 0, skipped: 0, failed: [] });
        onDone?.();
        return;
      }
      // If the backend fails with a server error (5xx), the per-employee overrides below are used instead.
      if (BACKEND.activeCycleDates) {
        const stage = STAGES.find(([k]) => k === form.stage)[1];
        try {
          await changeActiveCycleDates(cycle.id, { stage, opensOn: form.opensOn, closesOn: form.closesOn, reason: form.reason.trim() });
          setResult({ wholeCycle: true, changed: 0, skipped: 0, failed: [] });
          onDone?.();
          return;
        } catch (err) {
          if (!(Number(err?.response?.status) >= 500)) throw err; // validation errors are shown to HR
        }
      }
      const { data: members } = await getCycleMembers(cycle.id);
      const targets = members.filter((m) => m.eligibility === "ELIGIBLE" && !m.finalized);
      const stageType = STAGES.find(([k]) => k === form.stage)[1];
      const done = { changed: 0, skipped: members.length - targets.length, failed: [] };
      setProgress({ done: 0, total: targets.length });
      for (const m of targets) {
        try {
          await overrideDeadline(cycle.id, m.employeeId, { stage: stageType, opensOn: form.opensOn, closesOn: form.closesOn, reason: `Cycle dates changed: ${form.reason.trim()}` });
          done.changed += 1;
        } catch (err) {
          done.failed.push(`${m.employeeId}: ${getErrorMessage(err)}`);
        }
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
      setResult(done);
      if (done.changed) onDone?.();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(cycle)}
      size="md"
      title={`Change dates: ${cycle?.name || ""}`}
      subtitle={isDraft ? "The cycle is not launched yet, so the new dates are saved on the cycle." : "The cycle is running, so its dates are changed for every employee in it at once. Finalised appraisals are not changed."}
      onClose={close}
      footer={result ? <Button onClick={onClose}>Done</Button> : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button icon={FiCalendar} onClick={run} loading={busy}>Save new dates</Button>
        </>
      )}
    >
      {cycle && !result && (
        <div className="form-stack">
          <FormField label="Stage">
            <Select keepOrder value={form.stage} onChange={(e) => pickStage(e.target.value)}>
              {STAGES.map(([key]) => <option key={key} value={key}>{STAGE_LABELS[key]} (now {formatDate(current(key).startDate)} to {formatDate(current(key).endDate)})</option>)}
            </Select>
          </FormField>
          <div className="form-grid">
            <FormField label="Opens on"><input type="date" value={form.opensOn} min={cycle.startDate} max={cycle.endDate} onChange={(e) => setForm({ ...form, opensOn: e.target.value })} /></FormField>
            <FormField label="Closes on"><input type="date" value={form.closesOn} min={form.opensOn || cycle.startDate} max={cycle.endDate} onChange={(e) => setForm({ ...form, closesOn: e.target.value })} /></FormField>
          </div>
          <FormField label="Reason for the change" required>
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="For example: goal setting extended by one week (approved by Head of HR)" />
          </FormField>
          <Banner tone="info" title="What happens">Every employee in this cycle gets the new {STAGE_LABELS[form.stage].toLowerCase()} dates. Reminders follow the new deadline.</Banner>
          {progress && <><ProgressBar value={progress.total ? (progress.done / progress.total) * 100 : 100} label="Saving new dates" /><p className="muted">{progress.done} of {progress.total} employees</p></>}
          {error && <Banner tone="danger" title="Not saved">{error}</Banner>}
        </div>
      )}
      {result && (
        <Banner tone={result.failed.length ? "warning" : "success"} title={result.draft ? "New dates saved" : result.wholeCycle ? "New dates saved for the whole cycle" : `New dates saved for ${result.changed} ${result.changed === 1 ? "employee" : "employees"}`}>
          {result.wholeCycle && !result.draft && <div>Every appraisal in this cycle now uses the new dates. A personal deadline override set earlier for one employee still applies to that employee.</div>}
          {result.skipped > 0 && <div>{result.skipped} not changed (excluded or already finalised).</div>}
          {result.failed.length > 0 && <div>Failed: {result.failed.join(" ")}</div>}
        </Banner>
      )}
    </Modal>
  );
}
